import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, Download, ImagePlus, Loader2, RotateCcw, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { adminPartnerFileUrl, errorText, formatDay, formatDateTime } from "@/lib/partnerApi";

export const PARTNERS_CHANGED = "admin:partners-changed";
export const notifyPartners = () => window.dispatchEvent(new Event(PARTNERS_CHANGED));

export const ADMIN_STATUS = {
    pending: ["Pendente", "bg-amber-100 text-amber-800"],
    changes_requested: ["Em correção", "bg-orange-100 text-orange-800"],
    approved: ["Aprovada", "bg-emerald-100 text-emerald-800"],
    rejected: ["Rejeitada", "bg-stone-200 text-stone-700"],
};
export const PILL = "text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full whitespace-nowrap font-semibold";
const FIELD = "w-full px-3 py-2 border border-stone-300 rounded text-sm";
const LABEL = "block text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1";
const FILTERS = [["pending", "Pendentes"], ["changes_requested", "Em correção"], ["approved", "Aprovadas"], ["rejected", "Rejeitadas"], ["all", "Todas"]];

export function StatusChip({ status, testid }) {
    const [label, cls] = ADMIN_STATUS[status] || ADMIN_STATUS.pending;
    return <span className={`${PILL} ${cls}`} data-testid={testid}>{label}</span>;
}

export function PartnerUpdatesAdmin() {
    const [status, setStatus] = useState("pending");
    const [items, setItems] = useState(null);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(() => {
        api.get("/admin/partner-updates", { params: { status } }).then(({ data }) => setItems(data)).catch(() => setItems([]));
    }, [status]);
    useEffect(() => { setItems(null); load(); }, [load]);

    return (
        <div data-testid="admin-partner-updates">
            <div className="flex gap-2 flex-wrap mb-4">
                {FILTERS.map(([key, label]) => (
                    <button key={key} type="button" onClick={() => setStatus(key)} data-testid={`partner-updates-filter-${key}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${status === key ? "bg-[hsl(var(--forest))] text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-stone-300"}`}>
                        {label}
                    </button>
                ))}
            </div>
            {items === null ? <Loader2 className="w-5 h-5 animate-spin text-stone-400" /> : items.length === 0 ? (
                <p className="text-sm text-stone-400 italic" data-testid="partner-updates-empty">Sem atualizações neste estado.</p>
            ) : (
                <ul className="space-y-2">{items.map((u) => <li key={u.id}><UpdateRow u={u} onOpen={setOpenId} /></li>)}</ul>
            )}
            <ReviewUpdateDialog id={openId} onClose={() => setOpenId(null)} onChanged={() => { load(); notifyPartners(); }} />
        </div>
    );
}

export function UpdateRow({ u, onOpen }) {
    return (
        <button type="button" onClick={() => onOpen(u.id)} className="w-full text-left bg-white rounded-lg border border-stone-200 p-4 hover:border-[hsl(var(--copper))] transition-colors" data-testid={`partner-update-row-${u.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-semibold text-stone-900 truncate">{u.obra_title}</div>
                    <div className="text-xs text-stone-500 truncate">{u.lead_name} · {u.partner_name}</div>
                </div>
                <StatusChip status={u.status} testid={`partner-update-row-status-${u.id}`} />
            </div>
            <div className="text-xs text-stone-500 mt-2">
                Dia dos trabalhos: <span className="capitalize">{formatDay(u.work_date)}</span> · Enviada {formatDateTime(u.last_submitted_at)} · {u.photo_count} foto{u.photo_count === 1 ? "" : "s"}
            </div>
            <p className="text-sm text-stone-700 mt-1 line-clamp-2">{u.works_done}</p>
            {(u.resubmitted || u.issues) && (
                <div className="flex gap-1.5 mt-2 flex-wrap">
                    {u.resubmitted && <span className={`${PILL} bg-sky-100 text-sky-800`}>Corrigida e reenviada</span>}
                    {u.issues && <span className={`${PILL} bg-red-100 text-red-800`}>Com problemas</span>}
                </div>
            )}
        </button>
    );
}

export function ReviewUpdateDialog({ id, onClose, onChanged }) {
    const [d, setD] = useState(null);
    const load = useCallback(() => {
        if (!id) return;
        api.get(`/admin/partner-updates/${id}`).then(({ data }) => setD(data)).catch((e) => toast.error(errorText(e)));
    }, [id]);
    useEffect(() => { setD(null); load(); }, [load]);
    const done = () => { onChanged(); load(); };

    return (
        <Dialog open={!!id} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="review-update-dialog">
                <DialogHeader><DialogTitle className="font-display text-xl text-[hsl(var(--forest))] pr-6">{d?.obra_title || "Atualização do parceiro"}</DialogTitle></DialogHeader>
                {!d ? <Loader2 className="w-5 h-5 animate-spin text-stone-400" /> : (
                    <div className="space-y-5">
                        <ReviewMeta d={d} />
                        <PartnerContent d={d} />
                        {["pending", "changes_requested"].includes(d.status) ? <PublishForm d={d} onDone={done} /> : <Outcome d={d} />}
                        <RevisionHistory d={d} />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function ReviewMeta({ d }) {
    return (
        <div className="text-sm text-stone-600 space-y-1" data-testid="review-meta">
            <div className="flex flex-wrap items-center gap-2"><StatusChip status={d.status} testid="review-status" /><span className="font-semibold text-stone-900">{d.partner_name}</span><span>· Cliente: {d.lead_name}</span></div>
            <div>Dia dos trabalhos: <strong className="capitalize">{formatDay(d.work_date)}</strong></div>
            <div>Enviada em {formatDateTime(d.submitted_at)}{d.last_submitted_at !== d.submitted_at && <> · última versão {formatDateTime(d.last_submitted_at)}</>}</div>
            {d.status === "changes_requested" && d.admin_note && <div className="text-orange-800">Correção pedida: {d.admin_note}</div>}
        </div>
    );
}

function PhotoThumb({ id, info, selected, onToggle }) {
    return (
        <li className="relative">
            <a href={adminPartnerFileUrl(id)} target="_blank" rel="noreferrer" className="block aspect-square rounded overflow-hidden bg-stone-100">
                <img src={adminPartnerFileUrl(id)} alt="" className={`w-full h-full object-cover ${onToggle && !selected ? "opacity-35" : ""}`} loading="lazy" />
            </a>
            {onToggle && (
                <button type="button" onClick={() => onToggle(id)} className={`absolute top-1 left-1 w-7 h-7 rounded-full flex items-center justify-center ${selected ? "bg-emerald-600 text-white" : "bg-white/90 text-stone-500 border border-stone-300"}`} aria-label={selected ? "Retirar da publicação" : "Incluir na publicação"} data-testid={`review-photo-toggle-${id}`}>
                    {selected ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                </button>
            )}
            {info?.has_original && (
                <a href={adminPartnerFileUrl(id, true)} className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/65 text-white text-[10px] inline-flex items-center gap-1" data-testid={`review-photo-original-${id}`}>
                    <Download className="w-3 h-3" /> Original
                </a>
            )}
        </li>
    );
}

function PartnerContent({ d }) {
    const c = d.current || {};
    return (
        <section className="rounded-lg border border-stone-200 p-4 space-y-3" data-testid="review-partner-content">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Enviado pelo parceiro (versão atual)</div>
            <p className="text-sm text-stone-800 whitespace-pre-wrap">{c.works_done}</p>
            {c.issues && (
                <div className="rounded bg-red-50 border border-red-200 p-3 text-sm text-red-900" data-testid="review-issues">
                    <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="w-4 h-4" /> Problemas ou imprevistos (interno, não é publicado)</div>
                    <p className="mt-1 whitespace-pre-wrap">{c.issues}</p>
                </div>
            )}
            {c.next_works && <p className="text-sm text-stone-600 whitespace-pre-wrap"><strong>Próximos trabalhos:</strong> {c.next_works}</p>}
            {(c.photo_file_ids || []).length > 0 && (
                <ul className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {c.photo_file_ids.map((id) => <PhotoThumb key={id} id={id} info={d.photos[id]} />)}
                </ul>
            )}
        </section>
    );
}

function PublishForm({ d, onDone }) {
    const c = d.current || {};
    const [date, setDate] = useState(c.work_date || "");
    const [what, setWhat] = useState(c.works_done || "");
    const [next, setNext] = useState(c.next_works || "");
    const [note, setNote] = useState("");
    const [extra, setExtra] = useState([]);
    const [selected, setSelected] = useState(c.photo_file_ids || []);
    const [mode, setMode] = useState(null);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(null);
    const allPhotos = [...(c.photo_file_ids || []), ...extra];
    const canPublish = d.lead_status === "Execução";

    const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    const upload = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        setBusy("upload");
        for (const f of files) {
            const fd = new FormData();
            fd.append("file", f);
            try {
                const { data } = await api.post(`/admin/partner-updates/${d.id}/photos`, fd);
                setExtra((x) => [...x, data.id]);
                setSelected((s) => [...s, data.id]);
            } catch (err) {
                toast.error(errorText(err, "Erro ao enviar a fotografia"));
            }
        }
        setBusy(null);
    };
    const approve = async () => {
        setBusy("approve");
        try {
            await api.post(`/admin/partner-updates/${d.id}/approve`, {
                work_date: date, what_we_did: what, next_step: next, note, photo_file_ids: allPhotos.filter((id) => selected.includes(id)),
            });
            toast.success("Atualização publicada na Área do Cliente.");
            onDone();
        } catch (err) {
            toast.error(errorText(err));
        } finally {
            setBusy(null);
        }
    };
    const decide = async () => {
        setBusy("decide");
        try {
            await api.post(`/admin/partner-updates/${d.id}/${mode === "changes" ? "request-changes" : "reject"}`, { note: reason });
            toast.success(mode === "changes" ? "Pedido de correção enviado ao parceiro." : "Atualização rejeitada.");
            setMode(null);
            onDone();
        } catch (err) {
            toast.error(errorText(err));
        } finally {
            setBusy(null);
        }
    };

    return (
        <section className="rounded-lg border border-[hsl(var(--forest))]/25 bg-[hsl(var(--forest))]/[0.03] p-4 space-y-3" data-testid="review-publish-form">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--forest))] font-semibold">Publicação ao cliente (pode editar)</div>
            {!canPublish && <p className="text-xs text-amber-800 bg-amber-50 rounded p-2" data-testid="review-not-execution">A obra ainda não está em execução. Inicie a execução na ficha do cliente para poder publicar.</p>}
            <div className="grid sm:grid-cols-[180px_1fr] gap-3">
                <label><span className={LABEL}>Data da atualização</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={FIELD} data-testid="review-date" /></label>
                <label><span className={LABEL}>O que fizemos *</span><textarea rows={3} value={what} onChange={(e) => setWhat(e.target.value)} className={`${FIELD} resize-y`} data-testid="review-what" /></label>
            </div>
            <label className="block"><span className={LABEL}>O que acontece a seguir *</span><textarea rows={2} value={next} onChange={(e) => setNext(e.target.value)} className={`${FIELD} resize-y`} data-testid="review-next" /></label>
            <label className="block"><span className={LABEL}>Nota para o cliente (opcional)</span><textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={`${FIELD} resize-y`} data-testid="review-note" /></label>
            <div>
                <span className={LABEL}>Fotografias a publicar ({selected.length})</span>
                <ul className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {allPhotos.map((id) => <PhotoThumb key={id} id={id} info={d.photos[id]} selected={selected.includes(id)} onToggle={toggle} />)}
                    <li>
                        <label className="aspect-square rounded border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-[11px] text-stone-500 cursor-pointer hover:border-[hsl(var(--copper))]" data-testid="review-add-photo">
                            {busy === "upload" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Acrescentar
                            <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={upload} data-testid="review-add-photo-input" />
                        </label>
                    </li>
                </ul>
            </div>
            <p className="text-[11px] text-stone-500">O cliente vê a atualização no formato habitual, como publicada por O Homem da Obra. O registo original do parceiro fica guardado.</p>
            {mode ? (
                <div className="space-y-2" data-testid="review-decision-form">
                    <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={mode === "changes" ? "O que deve o parceiro corrigir?" : "Motivo da rejeição (fica no histórico)"} className={FIELD} data-testid="review-decision-text" />
                    <div className="flex gap-2 flex-wrap">
                        <button type="button" onClick={decide} disabled={!reason.trim() || !!busy} className="px-4 py-2 rounded-full bg-stone-900 text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-40" data-testid="review-decision-confirm">
                            {busy === "decide" && <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />}{mode === "changes" ? "Enviar pedido de correção" : "Confirmar rejeição"}
                        </button>
                        <button type="button" onClick={() => setMode(null)} className="px-4 py-2 text-xs text-stone-500" data-testid="review-decision-cancel">Cancelar</button>
                    </div>
                </div>
            ) : (
                <div className="flex gap-2 flex-wrap pt-1">
                    <button type="button" onClick={approve} disabled={!canPublish || !what.trim() || !next.trim() || !!busy} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[hsl(var(--forest))] text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-40" data-testid="review-approve">
                        {busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Aprovar e publicar
                    </button>
                    {d.status === "pending" && (
                        <button type="button" onClick={() => { setMode("changes"); setReason(""); }} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-orange-300 text-orange-800 bg-orange-50 text-xs font-semibold uppercase tracking-wider" data-testid="review-request-changes">
                            <RotateCcw className="w-4 h-4" /> Pedir correção
                        </button>
                    )}
                    <button type="button" onClick={() => { setMode("reject"); setReason(""); }} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-stone-300 text-stone-700 text-xs font-semibold uppercase tracking-wider" data-testid="review-reject">
                        <X className="w-4 h-4" /> Rejeitar
                    </button>
                </div>
            )}
        </section>
    );
}

function Outcome({ d }) {
    if (d.status === "rejected") {
        return <p className="rounded-lg bg-stone-100 p-3 text-sm text-stone-800" data-testid="review-rejected">Rejeitada. Motivo: {d.reject_reason}</p>;
    }
    const p = d.published_content || {};
    return (
        <section className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4 text-sm space-y-1" data-testid="review-published">
            <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Publicada ao cliente em {formatDateTime(d.published_at)}</div>
            <p className="whitespace-pre-wrap text-stone-800">{p.what_we_did}</p>
            <p className="text-stone-600"><strong>A seguir:</strong> {p.next_step}</p>
            <Link to={`/admin/clientes?lead=${d.lead_id}`} className="inline-block mt-1 text-xs font-semibold text-[hsl(var(--copper))] underline" data-testid="review-open-lead">Abrir ficha do cliente</Link>
        </section>
    );
}

const KIND_LABEL = { submitted: "Versão original do parceiro", revised: "Revisão do parceiro", resubmitted: "Corrigida e reenviada pelo parceiro", admin_publish: "Versão publicada (administrador)" };
const ACTION_LABEL = { changes_requested: "Correção pedida", rejected: "Rejeitada", approved: "Aprovada e publicada" };

function RevisionHistory({ d }) {
    const [open, setOpen] = useState(null);
    const decisions = (d.history || []).filter((h) => ACTION_LABEL[h.action]);
    return (
        <section data-testid="review-history">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-2">Histórico</div>
            <ol className="space-y-1.5">
                {(d.revisions || []).map((r) => {
                    const c = r.content || {};
                    return (
                        <li key={r.n} className="border border-stone-200 rounded p-2 text-xs">
                            <button type="button" onClick={() => setOpen(open === r.n ? null : r.n)} className="w-full text-left flex justify-between gap-2" data-testid={`review-revision-${r.n}`}>
                                <span><strong>{r.n}. {KIND_LABEL[r.kind] || r.kind}</strong> · {formatDateTime(r.at)}{r.by !== "partner" && ` · ${r.by}`}</span>
                                <span className="text-[hsl(var(--copper))]">{open === r.n ? "Fechar" : "Ver"}</span>
                            </button>
                            {open === r.n && (
                                <div className="mt-2 space-y-1 text-stone-700 whitespace-pre-wrap" data-testid={`review-revision-content-${r.n}`}>
                                    <div>Data: {c.work_date}</div>
                                    <div>{c.works_done || c.what_we_did}</div>
                                    {c.issues && <div className="text-red-800">Problemas: {c.issues}</div>}
                                    {(c.next_works || c.next_step) && <div>A seguir: {c.next_works || c.next_step}</div>}
                                    {c.note && <div>Nota: {c.note}</div>}
                                    <div>{(c.photo_file_ids || []).length} fotografia(s)</div>
                                </div>
                            )}
                        </li>
                    );
                })}
                {decisions.map((h, i) => (
                    <li key={`h${i}`} className="text-xs text-stone-600 px-2">{formatDateTime(h.at)} · <strong>{ACTION_LABEL[h.action]}</strong>{h.note && `: ${h.note}`} {h.by && `(${h.by})`}</li>
                ))}
            </ol>
        </section>
    );
}
